import { Box, FormControlLabel, Switch, useTheme } from "@mui/material";
import { setupTooltips } from "../../common/utilities/ChartHelpers";
import Highcharts, { YAxisOptions } from "highcharts";
import accessibility from "highcharts/modules/accessibility";
import exporting from "highcharts/modules/exporting";
import noData from "highcharts/modules/no-data-to-display";
import offlineExporting from "highcharts/modules/offline-exporting";
// KG: highcharts-react wrapper has rendering issue with scatter chart, so I had to revert to using highcharts without the wrapper for now
//import HighchartsReact from "highcharts-react-official";
import { forwardRef, Ref, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import GraphUtils from "../../common/utilities/GraphUtils";
import htmlParse from 'html-react-parser';
import plotlineimage from "./plugins/plotline-image";
import useGraphState from "./GraphState";
import { GraphItemOptions, GraphOptions } from "../../services/trending-data-service/Models";
import Enumerable from "linq";
import useSize from "../../hooks/use-size/useSize";
import { isIOS, isMobile, isMobileSafari, isSafari, isTablet } from "react-device-detect";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { themifyHighchartsOptions } from "../../common/utilities/ThemifyHighchartsOptions";
import { FeatureConstants } from "../../services/features-data-service/Models";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";

accessibility(Highcharts);
exporting(Highcharts);
noData(Highcharts);
offlineExporting(Highcharts);

const tipXMargin = 200;

export type GraphType = {
    reflow: () => void;
    reset: () => void;
    saveGraphImage: () => void;
    graphUpdated: () => void;
};

export type GraphProps = {
    contextObject?: IeLynxObject | undefined;
    defaultShowAxis?: boolean
    options: GraphOptions;
    showAxesToggle?: boolean;
    showTitle?: boolean;
    ignoreScrollTop?: boolean | undefined;
};

function Graph({
    contextObject,
    defaultShowAxis,
    options,
    showAxesToggle,
    showTitle,
    ignoreScrollTop
}: GraphProps, ref: Ref<unknown> | undefined) {

    const isPhone = isMobile && !isTablet;

    // User Story 53901: Stacked Trend Alignment
    const [recreatedCharts, setRecreatedCharts] = useState(false);
    const [marginRight, setMarginRight] = useState<number>();
    const [marginLeft, setMarginLeft] = useState<number>();

    const [appSettings] = useAppSettings();
    const theme = useTheme();
    const { t } = useTranslation();
    const [userState] = useUserContext();

    // Does this need moved to the sidebar?
    const [showAxesLabel, setShowAxesLabel] = useState<string>(t('trending.hideAxes') as string);

    const {
        state,
        
        setAllowAxesToggle,
        setCaptureMouseMove,
        setGraphHeight,
        setGraphParent,
        setShowAxes,
        setTipMaxWidth,
        setTipOpen,
        setTipPosition,
        setTipText,
        resetState
    } = useGraphState();


    const { data: trendPlotWidthAlignmentFeature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.TrendPlotWidthAlignment,
    });

    const trendPlotWidthAlignment = (trendPlotWidthAlignmentFeature ?? false) && !isPhone;
    

    const gridOptions = useMemo(() => {
        return {
            ...options,
            optionItems: options.optionItems.map(opt => themifyHighchartsOptions<GraphItemOptions>(opt, theme))
        };
    }, [
        options,
        theme
    ]);

    // When a Graph is displayed, only load plotline-image.js one time per Highcharts Instance
    //  and attach it to Highcharts as a Highcharts plugin.
    useEffect(() => {
        // This plotline image plugin enables drawing of the Settings Change and Event icons.
        plotlineimage(Highcharts);
    }, [state.chartInstances])

    useImperativeHandle<unknown, GraphType>(
        ref,
        () => ({
            reflow: reflow,
            reset: reset,
            saveGraphImage: saveGraphImage,
            graphUpdated: graphUpdated
        })
    );

    const graphUpdated = useCallback(() => {

        // the configuration changed, clear the right margin to force
        // recalculation
        if (trendPlotWidthAlignment) {

            setMarginRight(undefined);
            setMarginLeft(undefined);
        }
        

    }, [
        setMarginRight,
        setMarginLeft,
        trendPlotWidthAlignment
    ]);

    const reflow = useCallback(() => {  

        if ((state.chartInstances.size ?? 0) > 0) {

            if (trendPlotWidthAlignment) {

                if (state.showAxes) {

                    state.chartInstances.forEach(chartInstance => {

                        if (marginRight) {
                            chartInstance.options.chart!.marginRight = marginRight;
                        }

                        if (marginLeft) {
                            chartInstance.options.chart!.marginLeft = marginLeft;
                        }


                        chartInstance.redraw();                    
                        chartInstance.reflow();
                    });
                } else {
                    state.chartInstances.forEach(chartInstance => {
                        chartInstance.reflow();
                    });

                }
            } else {
                  state.chartInstances.forEach(chartInstance => chartInstance.reflow());                
            }
        }

        // original
        //   ((state.chartInstances.size ?? 0) > 0) &&
        //             state.chartInstances.forEach(chartInstance => chartInstance.reflow());            
        //         }

    }, [
        state.chartInstances,
        marginRight,
        marginLeft,
        state.showAxes,
        trendPlotWidthAlignment
    ]);


    // User Story 53901: Stacked Trend Alignment
    const internalResize = useCallback((redraw: boolean)  => {

        //console.log('internalResize')

        //let ndx = 0;

        const _marginRight = state.chartInstances.size > 1 ? marginRight : undefined;
        const _marginLeft = state.chartInstances.size > 1 ? marginLeft : undefined;

        state.chartInstances.forEach(chartInstance => {

            //console.log(`xxx - ${ndx}  marginRight: ${_marginRight}`);

            if (_marginRight) {

                // the whole point here is to trick highcharts into actually rerendering, this is why _marginRight + 1 (hack!)
                if (chartInstance.options.chart!.marginRight && chartInstance.options.chart!.marginRight > _marginRight) {
                    chartInstance.options.chart!.marginRight = _marginRight;    
                } else {
                    chartInstance.options.chart!.marginRight = _marginRight + 1;    
                }
            } else {
                chartInstance.options.chart!.marginRight = undefined;
            }

            if (_marginLeft) {

                // the whole point here is to trick highcharts into actually rerendering, this is why _marginRight + 1 (hack!)
                if (chartInstance.options.chart!.marginLeft && chartInstance.options.chart!.marginLeft > _marginLeft) {
                    chartInstance.options.chart!.marginLeft = _marginLeft;    
                } else {
                    chartInstance.options.chart!.marginLeft = _marginLeft + 1;    
                }
            } else {
                chartInstance.options.chart!.marginLeft = undefined;
            }


            //ndx++;

            if (redraw) {
                chartInstance.redraw();
            } else {
                chartInstance.reflow();
            }
        });


    }, [
        state.chartInstances,
        marginRight,
        marginLeft
    ]);


    const updateShowAxis = useCallback((checked: boolean) => {

        setShowAxes(checked);
        setShowAxesLabel(t(checked ? 'trending.hideAxes' : 'trending.showAxes') as string);

        if ((state.chartInstances.size ?? 0) > 0) {

            state.chartInstances.forEach(chartInstance => {

                GraphUtils.updateAxesVisibility(chartInstance, checked);

                // User Story 53901: Stacked Trend Alignment - only do this hack if feature flag is enabled
                if (trendPlotWidthAlignment && marginRight) {
                    if (!checked) {
                        chartInstance.options.chart!.marginRight = undefined;
                        chartInstance.redraw();
                    } else {
                        chartInstance.options.chart!.marginRight = marginRight;
                        chartInstance.reflow();
                    }
                }

                if (trendPlotWidthAlignment && marginLeft) {
                    // if (!checked) {
                    //     chartInstance.options.chart!.marginLeft = undefined;
                    //     chartInstance.redraw();
                    // } else {
                    //     chartInstance.options.chart!.marginLeft = marginLeft;
                    //     chartInstance.reflow();
                    // }
                        chartInstance.options.chart!.marginLeft = marginLeft;
                        chartInstance.reflow();

                }

            });

            if (!checked) {
                setMarginRight(undefined);
                //setMarginLeft(undefined);
            }
        }
    }, [
        setShowAxes,
        state.chartInstances,
        t,
        setMarginRight,
        marginRight,
        setMarginLeft,
        marginLeft,
        reflow,
    ]);

    useEffect(() => {

        if (defaultShowAxis !== undefined) {
            updateShowAxis(defaultShowAxis);
        }

    }, [
        defaultShowAxis,
        updateShowAxis
    ]);

    // The size for the scrolling parent container for all the Graphs.
    const graphContTargetSize = useSize(state.graphParent);

    useEffect(() => {

        if (graphContTargetSize && (gridOptions.isComparison || gridOptions.isStacked)) {

            // Comparison Graphs are always 2 in the viewport. Stacked Graphs default to 2 in the viewport.
            const viewPortChartNum: number = gridOptions.isStacked ? userState.userSettings?.trendingNumberChartsInViewport ?? 2 : 2;

            const graphHeight = GraphUtils.getGraphHeight(graphContTargetSize.height, gridOptions?.optionItems?.length,
                viewPortChartNum);

            setGraphHeight(graphHeight);
            // Make sure all the graphs are resized to the new size after the containers have been
            // resized.
            GraphUtils.resizeWithTimer(state.chartInstances, state.showAxes);
        } else {
            setGraphHeight(undefined);
        }
    }, [
        graphContTargetSize,
        gridOptions.isComparison,
        gridOptions.isStacked,
        gridOptions?.optionItems?.length,
        setGraphHeight,
        state.chartInstances,
        state.chartInstances.size,
        state.showAxes,
        userState.userSettings?.trendingNumberChartsInViewport
    ]);

    const reset = () => {

        //console.log("*** - RESET!!!")
        GraphUtils.resetScrollPosition(state.graphParent);
        resetState();
        resetGraphInstances();
        resetHiddenSeries();
    }

    const resetGraphInstances = () => {

        //        console.log("*** - RESET CHART INSTANCES!!!")

        // If we set a new Map of chart instances instead of just clearing it the 
        // chart gets recreated when it shouldn't
        state.chartInstances && state.chartInstances.clear();
    }

    const resetHiddenSeries = () => {

        //      console.log("*** - RESET HIDDEN SERIES!!!")
        // If we set a new Map of hidden series instead of just clearing it the 
        // chart gets recreated when it shouldn't
        state.hiddenSeries && state.hiddenSeries.clear();
    }

    const saveGraphImage = () => {

        // One export per chart
        // ((state.chartInstances.size ?? 0) > 0) &&
        //     state.chartInstances.forEach(chartInstance => GraphUtils.saveGraphImage(chartInstance));

        // One export with all the charts
        ((state.chartInstances.size ?? 0) > 0)
            && GraphUtils.saveGraphImages(
                [...state.chartInstances.values()],
                {
                    //type: "application/pdf",
                    filename: "ChartExport"
                }
            );
    };

    useEffect(() => {

        state.chartInstances.clear();

        setTipOpen(false);

        
        gridOptions.optionItems.forEach((optionItems, chartIndex) => {

            if (optionItems) {

                if (!showTitle) {

                    optionItems.title = {
                        text: undefined
                    };
                }

                setupTooltips(optionItems?.xAxis as Highcharts.XAxisOptions[],
                    {
                        tipXMargin: tipXMargin,
                        setTipOpen: setTipOpen,
                        setTipColor: undefined,
                        setTipMaxWidth: setTipMaxWidth,
                        setTipPosition: setTipPosition,
                        setTipText: setTipText,
                        ignoreScrollTop: ignoreScrollTop
                    });

                gridOptions.synchronizeCharts && GraphUtils.setSynchronizedChartOptions(state.chartInstances, optionItems);

                GraphUtils.addLegendItemClickEvent(state.chartInstances, state.hiddenSeries, optionItems, chartIndex,
                    gridOptions.synchronizeLegends ?? false);

                // If the Legends are synchronized, we only need to use the first hidden series list.
                const hiddenSeriesItem: Array<string> | undefined = !gridOptions.synchronizeLegends ?
                    state.hiddenSeries.get(chartIndex) : state.hiddenSeries.get(0);

                // *** How do we figure out if this is a refresh and when the refresh is done? Or do we just clear it when a new 
                //  config is selected or there is a new object and other things???
                //if (refresh === true && hiddenSeries && hiddenSeries.length > 0 && optionItems.series) {
                GraphUtils.setSeriesVisible(optionItems, hiddenSeriesItem);
                GraphUtils.setListTextTooltip(optionItems, gridOptions.listTextTagMapping);

                //console.log(`*** - CREATE NEW CHART!!! ChartID: ${optionItems.id} Hidden Series: ${state.hiddenSeries.size} - Hidden Series Item: ${hiddenSeriesItem?.length ?? 0}`);

                const newChart = Highcharts.chart(optionItems.id, optionItems);

                if (gridOptions.synchronizeCharts) {
                    // Override the reset function, we don't need to hide the tooltips and
                    // crosshairs until the mouse moves off the captured Div
                    (newChart as any).pointer.reset = () => {
                        return undefined;
                    };
                }

                if (state.showAxes && trendPlotWidthAlignment) {
                    newChart.options.chart!.marginRight = marginRight;
                    newChart.options.chart!.marginLeft = marginLeft;
                }

                state.chartInstances.set({ id: optionItems.id, index: chartIndex }, newChart);
                
            }
        });

        const axesToggle = showAxesToggle &&
            Enumerable.from(gridOptions.optionItems)
                .any(opt => opt.yAxis?.hasOwnProperty('length') &&
                    (opt?.yAxis as YAxisOptions[]).length > 1 ? true : false);

        setAllowAxesToggle(axesToggle ?? false);

        if (!state.showAxes && axesToggle) {
            GraphUtils.updateAllAxesVisibility([...state.chartInstances.values()], state.showAxes);
        }

        if (trendPlotWidthAlignment) {

            // User Story 53901: Stacked Trend Alignment - only do this hack if feature flag is enabled
            // this will force margin to be recalculated.
            setRecreatedCharts(true);
        }

    }, [
        contextObject?.objectName,
        ignoreScrollTop,
        gridOptions,
        setAllowAxesToggle,
        setTipMaxWidth,
        setTipOpen,
        setTipPosition,
        setTipText,
        state.chartInstances,
        state.hiddenSeries,
        state.showAxes,
        showTitle,
        showAxesToggle,
        userState.userSettings?.trendingDataTooltipDisplay,
        marginRight,
        marginLeft,
        trendPlotWidthAlignment,
        setRecreatedCharts
    ]);

    useEffect(() => {

        GraphUtils.resizeWithTimer(state.chartInstances, state.showAxes);

    }, [
        appSettings.maximizeContent,
        state.chartInstances,
        state.chartInstances.size,
        state.showAxes
    ]);

    useEffect(() => {

        //if (!isMobileSafari && !isSafari) {

            if (state.captureMouseMove) {
                GraphUtils.SyncRemoveMouseMove(state.graphParent);
                setCaptureMouseMove(false);
            }

            if (gridOptions.synchronizeCharts && (state.chartInstances?.size ?? 0) > 0) {

                //console.log("***Add Listeners called.")
                
                GraphUtils.SyncAddMouseMouseMove(state.chartInstances, state.graphParent, !isIOS && !isMobileSafari && !isSafari);
                setCaptureMouseMove(true);
            }

            // cleanup
            return () => {

                //console.log("***Listener Cleanup called.")

                if (state.captureMouseMove) {
                    GraphUtils.SyncRemoveMouseMove(state.graphParent);
                    setCaptureMouseMove(false);
                }
            };
        //}

    }, [
        setCaptureMouseMove,
        state.captureMouseMove,
        gridOptions.synchronizeCharts,
        state.chartInstances,
        state.chartInstances.size,
        state.graphParent,

    ]);

    const onShowAxesChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {

        updateShowAxis(checked);
    };


    useEffect(() => {

        const initializeMargin = () => {

            if ((state.chartInstances.size ?? 0) > 0) {

                const values = [...state.chartInstances.values()];

                let widths = values.map(o => o.plotWidth);

                // do not adjust the margin if only a single chart in the collection
                if (widths.length < 2) {
                    setMarginRight(undefined);
                    setMarginLeft(undefined);
                    return;
                }

                const minPlotWidth = Math.min(...widths);
                const maxPlotWidth = Math.max(...widths);

                let value = Math.abs(maxPlotWidth - minPlotWidth) + 15; // a little extra margin to keep in the view port
                //console.log(`marginRight: ${value}`);

                if (widths.length > 1) {
                    if (marginRight !== value) {
                        setMarginRight(value);
                    }
                }

                widths = values.map(o => o.plotLeft);


                //const minPlotLeft = Math.min(...widths);
                const maxPlotLeft = Math.max(...widths);

                value = maxPlotLeft + 15; // a little extra margin to keep in the view port
                //console.log(`marginRight: ${value}`);

                if (widths.length > 1) {
                    if (marginLeft !== value) {
                        setMarginLeft(value);
                    }
                }



            }

        }

        if (trendPlotWidthAlignment) {

            if (recreatedCharts) {

                //if (state.showAxes && !marginRight && !marginLeft) {
                if (state.showAxes && !marginRight) {
                    initializeMargin();
                }

                setRecreatedCharts(false);
            }
        }

    }, [
        state.chartInstances,
        state.showAxes,
        marginRight,
        setMarginRight,
        marginLeft,
        setMarginLeft,
        trendPlotWidthAlignment,
        recreatedCharts,
        setRecreatedCharts
    ]);

    // 2026-04-28 - User Story 53901: Stacked Trend Alignment, this support is a hack to align the stacked
    // charts.  The only technique I have found to work is to set the right margin which will shrink the plot
    // width.  The reason for the various setTimeouts is that we have to allow highcharts to render the charts
    // to determine the amount of margin, apply to the various charts, and then try to get highcharts to render
    useEffect(() => {

        // highcharts redraw and reflow do not always force a rerender of the graph
        // highcharts observes (listens) to the window resize event and will then repaint itself.
        setTimeout(() => window.dispatchEvent(new Event('resize')));
        setTimeout(() => {
            internalResize(true);
        },1000);
        

    }, [
        recreatedCharts,
        state.chartInstances,
        state.showAxes
    ]);

    return (
        <>
            {
                state.allowAxesToggle &&
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}>

                    <FormControlLabel
                        sx={{ mr: (theme) => theme.spacing(1) }}
                        control={
                            <Switch
                                checked={state.showAxes}
                                onChange={onShowAxesChange}
                                slotProps={{
                                    input: { 'aria-label': 'controlled' }
                                }}
                            />

                        } label={showAxesLabel} labelPlacement='start' />
                </Box>
            }
            <Box
                id='graph-2'
                ref={setGraphParent}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: '1 1 auto',
                    mb: (theme) => theme.spacing(.25),
                    overflowX: 'hidden',
                    overflowY: state.graphHeight ? 'auto' : 'hidden',
                    '&:hover': {
                        cursor: 'default!important'
                    }
                }}>
                {gridOptions?.optionItems && gridOptions.optionItems.map((opt, index) =>

                    <Box
                        id={`graph-3-${index}`}
                        key={index}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: state.graphHeight ? `0 0 ${isPhone ? '400px' : state.graphHeight}` : `1 1 auto`,
                            mt: (gridOptions?.isComparison || gridOptions?.isStacked) ? theme.spacing(.5) : undefined,
                            position: 'relative'
                        }}>
                        {(gridOptions?.isComparison || gridOptions?.isStacked) &&

                            <Box sx={{
                                backgroundColor: "custom.background.1",
                                width: 1,
                                paddingLeft: 1,
                                display: "flex",
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {opt.headerTitle}
                            </Box>
                        }

                        <Box
                            id={opt.id ?? GraphUtils.getChartID()}
                            sx={{
                                position: 'absolute',
                                top: (gridOptions?.isComparison || gridOptions?.isStacked) ? theme.spacing(3) : 0,
                                bottom: 0,
                                left: 0,
                                right: 0,
                                mt: (gridOptions?.isComparison || gridOptions?.isStacked) ? theme.spacing(.5) : undefined
                            }}
                        >
                        </Box>
                    </Box>
                )}
            </Box>
            {state.tipOpen && state.tipPosition && state.tipText && <Box
                sx={{
                    position: 'absolute',
                    top: `${state.tipPosition?.y}px`,
                    left: `${state.tipPosition?.x}px`,
                    color: `black`,
                    // Font Highcharts uses for Tooltips
                    fontFamily: 'Roboto Condensed',
                    // Font Size Highcharts uses for Tooltips
                    fontSize: '12px',
                    // Font weight Highcharts uses for Tooltips
                    fontWeight: 'normal',
                    border: '1px solid #e8e8e8',
                    maxWidth: `${state.tipMaxWidth}px`,
                    minWidth: '80px',
                    padding: theme.spacing(.5),
                    margin: theme.spacing(.8),
                    backgroundColor: '#f7f7f7',
                    boxShadow: '2px 2px silver',
                    opacity: .85,
                    zIndex: 100,
                }}>
                {htmlParse(state.tipText)}
            </Box>}
            {/* </Box> */}
        </>
    );
}

export default forwardRef(Graph);